import React from 'react';
import './Renewal.css';
import logoImage from '../assets/images/top/logo-icon7.png';

/**
 * リブランディング告知ページ（mcie → AI・しごと学校）
 *
 * このコンポーネントは意図的に自己完結で作っています。
 * 新ドメイン側のプロジェクトへそのまま移植できるよう、
 * 共通ヘッダー/フッターや他セクションに強く依存させていません。
 *
 * ↓ 決まったら差し替える値はここに集約。
 */
const NEW_SITE_URL = 'https://www.ai-career-academy.com/';
const NEW_SITE_READY = true;                // 新サイト公開後 true に
const SWITCH_DATE = '';                      // 例: '2026年9月1日'（未定なら空文字）

const Renewal = () => {
  return (
    <div className="renewal">

      {/* 1. ヒーロー：宣言 ------------------------------------------------ */}
      <section className="rn-hero">
        <div className="rn-hero__inner">
          <p className="rn-hero__eyebrow">お知らせ</p>

          <div className="rn-namechange">
            <span className="rn-namechange__old">
              メタバース情報工学学校
            </span>
            <span className="rn-namechange__arrow" aria-hidden="true">→</span>
            <span className="rn-namechange__new">
              AI・しごと学校
            </span>
          </div>

          <h1 className="rn-hero__title">
            名前は変わります。<br />
            <span className="rn-grad">価値は、変わらないまま<br className="rn-br-sp" />進化します。</span>
          </h1>

          <p className="rn-hero__lead">
            「mcie / メタバース情報工学学校」は、
            <strong className="rn-nowrap">「AI・しごと学校」</strong>へ<br className="rn-br-pc" />生まれ変わります。
            {SWITCH_DATE && <><br />{SWITCH_DATE} 新サイト公開予定。</>}
          </p>

          <div className="rn-hero__cta">
            {NEW_SITE_READY ? (
              <a className="rn-btn rn-btn--primary" href={NEW_SITE_URL} target="_blank" rel="noopener noreferrer">
                新サイトを見る<i className="fa-solid fa-arrow-right" />
              </a>
            ) : (
              <span className="rn-btn rn-btn--disabled">新サイトは近日公開</span>
            )}
            <a className="rn-btn rn-btn--ghost" href="#why">名称変更の理由を読む</a>
          </div>
        </div>
        <div className="rn-hero__glow" aria-hidden="true" />
      </section>

      {/* 2. なぜ変わるのか（誤解を解く） -------------------------------- */}
      <section className="rn-why" id="why">
        <div className="rn-section__head">
          <p className="rn-kicker">Why we changed the name</p>
          <h2>これは、<br className="rn-br-sp" />方針転換ではありません。<br />「本当にやってきたこと」<br className="rn-br-sp" />を、<br className="rn-br-pc" />名前にしただけです。</h2>
        </div>

        <div className="rn-myth">
          <div className="rn-myth__card rn-myth__card--myth">
            <span className="rn-myth__label">よくある誤解</span>
            <p>「メタバースを教える<span className="rn-nowrap">学校でしょ？」</span></p>
          </div>
          <div className="rn-myth__vs" aria-hidden="true">実は</div>
          <div className="rn-myth__card rn-myth__card--truth">
            <span className="rn-myth__label">本当のところ</span>
            <p>最初から、AIで<span className="rn-nowrap">“しごと”</span>を<span className="rn-nowrap">手にいれる学校でした。</span></p>
          </div>
        </div>

        <div className="rn-reasons">
          <div className="rn-reason">
            <div className="rn-reason__icon"><i className="fa-solid fa-globe" /></div>
            <h3>メタバースは<span className="rn-nowrap">“教える対象”</span>ではなく<span className="rn-nowrap">“学びの場”</span></h3>
            <p>私たちが扱ってきたのはメタバースそのものではありません。学びの舞台<span className="rn-nowrap">（フィールド）</span>としてメタバースを選んでいただけでした。</p>
          </div>
          <div className="rn-reason">
            <div className="rn-reason__icon"><i className="fa-solid fa-people-group" /></div>
            <h3>選んだ理由は「繋がり」</h3>
            <p>場所や距離を越えて人と人がつながるのに、メタバースは最適だから。仲間・講師・仕事との出会いを生む場として活かしてきました。</p>
          </div>
          <div className="rn-reason">
            <div className="rn-reason__icon"><i className="fa-solid fa-robot" /></div>
            <h3>AIは、最初からカリキュラムの中心</h3>
            <p>開校当初からAIを扱ってきました。だから今回の改名は、看板を実態に合わせるためのもの。中身は最初から一貫しています。</p>
          </div>
        </div>
      </section>

      {/* 3. 変わらないこと × グレードアップ ----------------------------- */}
      <section className="rn-continuity">
        <div className="rn-section__head">
          <p className="rn-kicker">What stays &amp; what levels up</p>
          <h2><span className="rn-nowrap">変わらない安心。</span><span className="rn-grad rn-nowrap">さらにグレードアップ。</span></h2>
        </div>

        <div className="rn-ba">
          <div className="rn-ba__col rn-ba__col--stay">
            <h3><i className="fa-solid fa-shield-heart" />変わらないこと</h3>
            <ul>
              <li>AI中心のカリキュラム</li>
              <li>就職・独立・案件獲得までの<span className="rn-nowrap">サポート</span></li>
              <li>費用支援・給付の仕組み</li>
              <li>繋がりを育む学びの場<span className="rn-nowrap">（メタバース）</span></li>
              <li>目指すゴール＝自由な働き方</li>
            </ul>
          </div>
          <div className="rn-ba__col rn-ba__col--up">
            <h3><i className="fa-solid fa-arrow-trend-up" />グレードアップすること</h3>
            <ul>
              <li>最新AIに対応した<span className="rn-nowrap">カリキュラムの強化</span></li>
              <li><span className="rn-nowrap">“しごと”</span>に直結する<span className="rn-nowrap">実践コンテンツの拡充</span></li>
              <li>より分かりやすくなった学校の入口<span className="rn-nowrap">（名称・サイト）</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* 4. 新サイトのご案内 -------------------------------------------- */}
      <section className="rn-newsite">
        <div className="rn-newsite__card">
          <img src={logoImage} alt="AI・しごと学校" className="rn-newsite__logo" />
          <h2>新しいサイトは、<br className="rn-br-sp" />新しいドメインで。</h2>
          <p>
            「AI・しごと学校」は別ドメインで新しくオープンします。
            準備が整い次第、こちらからご案内します。それまでは、これまで通りこのサイトもご利用いただけます。
          </p>
          {NEW_SITE_READY ? (
            <a className="rn-btn rn-btn--primary rn-btn--lg" href={NEW_SITE_URL} target="_blank" rel="noopener noreferrer">
              新サイトへ移動<i className="fa-solid fa-arrow-right" />
            </a>
          ) : (
            <span className="rn-btn rn-btn--disabled rn-btn--lg">新サイトは近日公開予定</span>
          )}
        </div>
      </section>

      {/* 5. 在校生・検討中の方へ ---------------------------------------- */}
      <section className="rn-assure">
        <div className="rn-assure__inner">
          <i className="fa-solid fa-comment-dots rn-assure__icon" />
          <h2>在校生・入学を<br className="rn-br-sp" />検討中のみなさまへ</h2>
          <p>
            名前が変わっても、学べる内容もサポートも、これまで通りです。
            むしろ、より良くなってお迎えします。安心して学び続けてください。
          </p>
        </div>
      </section>

      {/* 6. CTA / フッター誘導 ------------------------------------------ */}
      <section className="rn-cta">
        <h2>ご不明な点は、お気軽に。</h2>
        <div className="rn-cta__buttons">
          <a className="rn-btn rn-btn--primary" href="https://lin.ee/4z5c3KI" target="_blank" rel="noopener noreferrer">
            LINEで相談
          </a>
          <a className="rn-btn rn-btn--ghost" href="https://forms.gle/ZCVbDRcB7TXLSGiM8" target="_blank" rel="noopener noreferrer">
            お問い合わせ
          </a>
        </div>
      </section>

    </div>
  );
};

export default Renewal;
